import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter, HashRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider } from './contexts/AuthContext';
import { PendingDecisionAudience } from '@/components/editorial/PendingDecisionAudience';
import { TooltipProvider } from '@/components/ui/tooltip';
import { isNative } from './lib/platform';
import { checkForBundleUpdate } from '@/lib/liveUpdates';
import { installChunkReloadHandler } from '@/lib/chunk-reload';
import { shouldRetryQuery } from '@/lib/query-retry';
import App from './App';
// Self-hosted fonts (B-462 / #895): the four families ship from our own origin —
// no request to Google Fonts. Weight parity with the former Google URL:
// Fraunces ital+opsz+wght, Inter wght, JetBrains Mono 400/500, Instrument Sans 400/500.
import '@fontsource-variable/fraunces/opsz.css';
import '@fontsource-variable/fraunces/opsz-italic.css';
import '@fontsource-variable/inter';
import '@fontsource/jetbrains-mono/400.css';
import '@fontsource/jetbrains-mono/500.css';
import '@fontsource/instrument-sans/400.css';
import '@fontsource/instrument-sans/500.css';
// Multilingual R1 (#2109): Noto Sans SC is the self-hosted CJK face. It sits
// after the Latin faces and before index.css so the token stacks can name it.
// Its 101 unicode-range slices download only for text that reaches it in the
// stack: Han, and any symbol neither the Latin faces nor the 'TRF Latin
// Symbols' local() shim in index.css can draw. With the shim in place the
// English /login walk fetched zero slices (it fetched two, 69,716 B, without it).
import '@fontsource-variable/noto-sans-sc';
import './index.css';
// All zh typography lives in one :lang(zh-Hans) stylesheet, wired right after
// index.css so its rules cascade last (research §2.3 rule 5).
import './styles/cjk.css';

const Router = isNative() ? HashRouter : BrowserRouter;

// Recover from stale-chunk dynamic-import failures after a web deploy (B-149).
installChunkReloadHandler();

// Create a React Query client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      refetchOnWindowFocus: false,
      retry: shouldRetryQuery, // one retry, never on a 4xx (#2252)
    },
  },
});

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <Router>
        <AuthProvider>
          <PendingDecisionAudience>
            <TooltipProvider delayDuration={300}>
              <App />
            </TooltipProvider>
          </PendingDecisionAudience>
        </AuthProvider>
      </Router>
    </QueryClientProvider>
  </React.StrictMode>
);

// OTA live updates (native only). notifyAppReady() confirms this bundle booted
// OK — Capgo auto-rolls-back to the last-good bundle if it's not called within
// the appReadyTimeout. Then check for a newer bundle in the background.
if (isNative()) {
  import('@capgo/capacitor-updater').then(({ CapacitorUpdater }) => {
    CapacitorUpdater.notifyAppReady();
    void checkForBundleUpdate();
  });
}
